import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import AccountsSubNav from '../components/AccountsSubNav'
import SharedFilters from '../../../../components/SharedFilters'
import AppActionMenu from '../../../../components/AppActionMenu'
import AppPagination from '../../../../components/AppPagination'
import { appToast } from '../../../../components/AppToast'
import { appConfirm } from '../../../../components/AppConfirmModal'
import { listAccountHeads, archiveAccountHead, deleteAccountHead, getAcGroups } from '../heads/api/acHeadApi'

export default function BankListScreen() {
  const navigate = useNavigate()
  const [companyFilter, setCompanyFilter] = useState('All')
  const [branchFilter, setBranchFilter] = useState('All')
  const [departmentFilter, setDepartmentFilter] = useState('All')
  const [placeFilter, setPlaceFilter] = useState('All')
  const [bankNameFilter, setBankNameFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('Active')
  const [searchField, setSearchField] = useState('Name')
  const [searchText, setSearchText] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [page, setPage] = useState(1)
  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [bankGroupId, setBankGroupId] = useState(null)

  useEffect(() => {
    getAcGroups().then(groups => {
      const bankGroup = groups.find(g => (g.Name || '').toLowerCase().includes('bank'))
      if (bankGroup) setBankGroupId(bankGroup.ID)
    }).catch(() => {})
  }, [])

  const loadData = useCallback(async () => {
    setIsLoading(true)
    try {
      const params = {
        searchText: searchText || bankNameFilter || undefined,
        archive: statusFilter === 'All' ? undefined : (statusFilter === 'Archive' ? 1 : 0),
        groupId: bankGroupId || undefined,
        page,
        pageSize: 25,
      }
      const data = await listAccountHeads(params)
      const rows = (Array.isArray(data) ? data : data?.items || []).map(row => ({
        id: row.ID,
        bankAc: row.Name,
        entries: row.CostCenter || 0,
        opBal: row.Budget || 0.0,
        dr: 0.0,
        cr: 0.0,
        balance: row.Budget || 0.0,
        status: row.Archive ? 'Archive' : 'Active',
      }))
      setItems(rows)
    } catch (e) {
      console.error('Failed to load bank accounts', e)
    } finally {
      setIsLoading(false)
    }
  }, [searchText, bankNameFilter, statusFilter, bankGroupId, page])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleReset = () => {
    setCompanyFilter('All')
    setBranchFilter('All')
    setDepartmentFilter('All')
    setPlaceFilter('All')
    setBankNameFilter('')
    setStatusFilter('Active')
    setSearchField('Name')
    setSearchText('')
    setFromDate('')
    setToDate('')
    setPage(1)
  }

  const handleArchive = async (id) => {
    try {
      await archiveAccountHead(id)
      appToast.success('Bank account status updated.')
      loadData()
    } catch (e) {
      appToast.error('Failed to update bank account status.')
    }
  }

  const handleDelete = async (id, bankAc) => {
    const confirmed = await appConfirm({
      title: 'Delete Bank Account',
      message: `Delete Bank Account "${bankAc}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    })
    if (!confirmed) return

    try {
      await deleteAccountHead(id)
      appToast.success(`Bank account "${bankAc}" deleted successfully.`)
      loadData()
    } catch (e) {
      appToast.error('Failed to delete bank account.')
    }
  }

  const filteredItems = items

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Header */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Financial Master</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Bank Accounts
          </h1>
        </div>
        <AccountsSubNav />
      </section>

      {/* Shared Filters Accordion */}
      <SharedFilters
        searchField={searchField}
        searchText={searchText}
        statusFilter={statusFilter}
        companyFilter={companyFilter}
        branchFilter={branchFilter}
        departmentFilter={departmentFilter}
        placeFilter={placeFilter}
        bankNameFilter={bankNameFilter}
        fromDate={fromDate}
        toDate={toDate}

        setSearchField={setSearchField}
        setSearchText={setSearchText}
        setStatusFilter={setStatusFilter}
        setCompanyFilter={setCompanyFilter}
        setBranchFilter={setBranchFilter}
        setDepartmentFilter={setDepartmentFilter}
        setPlaceFilter={setPlaceFilter}
        setBankNameFilter={setBankNameFilter}
        setFromDate={setFromDate}
        setToDate={setToDate}
        onReset={handleReset}

        searchFieldOptions={[{ value: 'Name', label: 'Bank Name' }]}
        statusOptions={[{ value: 'Active', label: 'Active' }, { value: 'Archive', label: 'Archived' }, { value: 'All', label: 'All' }]}
        companyOptions={[{ value: 'All', label: 'All Companies' }]}
        branchOptions={[{ value: 'All', label: 'All Branches' }]}
        departmentOptions={[{ value: 'All', label: 'All Departments' }]}
        placeOptions={[{ value: 'All', label: 'All Places' }]}
        visibleAccordionFilters={['company', 'branch', 'department', 'place', 'bankName', 'fromDate', 'toDate']}
      />

      {/* Data Table */}
      <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[850px] text-left text-sm">
            <thead>
              <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant">
                <th className="px-5 py-3.5">Bank A/c</th>
                <th className="px-5 py-3.5 text-center">Entries</th>
                <th className="px-5 py-3.5 text-right">OpBal</th>
                <th className="px-5 py-3.5 text-right">Dr</th>
                <th className="px-5 py-3.5 text-right">Cr</th>
                <th className="px-5 py-3.5 text-right">Balance</th>
                <th className="w-20 px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/15">
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-sm text-on-surface-variant font-medium">
                    No entries meet selected criteria.
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => (
                  <tr key={item.id} className="transition-colors hover:bg-surface-container-low/40">
                    <td className="px-5 py-3.5 font-bold text-on-surface">{item.bankAc}</td>
                    <td className="px-5 py-3.5 text-center font-bold text-sm text-on-surface font-body tracking-tight tabular-nums">{item.entries}</td>
                    <td className="px-5 py-3.5 text-right font-bold text-sm text-on-surface font-body tracking-tight tabular-nums">₹{item.opBal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td className="px-5 py-3.5 text-right font-bold text-sm text-emerald-600 font-body tracking-tight tabular-nums">₹{item.dr.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td className="px-5 py-3.5 text-right font-bold text-sm text-amber-600 font-body tracking-tight tabular-nums">₹{item.cr.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td className="px-5 py-3.5 text-right font-bold text-sm text-on-surface font-body tracking-tight tabular-nums">₹{item.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td className="px-5 py-3.5 text-right">
                      <AppActionMenu
                        triggerLabel=""
                        triggerIcon="more_vert"
                        showChevron={false}
                        triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent"
                        items={[
                          { key: 'edit', label: 'Edit', icon: 'edit' },
                          { key: 'archive', label: item.status === 'Active' ? 'Archive' : 'Unarchive', icon: item.status === 'Active' ? 'archive' : 'unarchive' },
                          { key: 'delete', label: 'Delete', icon: 'delete' },
                          { key: 'reconcile', label: 'Reconcile', icon: 'check_circle' },
                          { key: 'auto-reco', label: 'Auto-Reco', icon: 'autorenew' },
                          { key: 'undo-reco', label: 'Undo-Reco', icon: 'undo' },
                        ]}
                        onSelect={(key) => {
                          if (key === 'edit') navigate(`/masters/acheads/banks/entry?id=${item.id}`)
                          else if (key === 'archive') handleArchive(item.id)
                          else if (key === 'delete') handleDelete(item.id, item.bankAc)
                          else if (key === 'reconcile') appToast.info(`Starting manual reconciliation for bank account "${item.bankAc}"`)
                          else if (key === 'auto-reco') appToast.success(`Auto-reconciliation completed successfully for bank account "${item.bankAc}"`)
                          else if (key === 'undo-reco') appToast.warning(`Undid reconciliation for bank account "${item.bankAc}"`)
                        }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row">
          <p className="text-xs text-on-surface-variant font-medium">
            Showing {filteredItems.length} entries
          </p>
          <AppPagination
            page={page}
            totalPages={1}
            onPageChange={setPage}
            canPrevious={false}
            canNext={false}
          />
        </div>
      </section>
    </div>
  )
}
